import { useEffect, useState } from "react";
import { apiJson } from "./api";

export type AutopilotRuntimeState = "observing" | "acting" | "blocked" | "complete";

export interface AutopilotTaskStatus {
  id: string;
  prompt: string;
  status: string;
  created_at: string;
  updated_at: string;
}

export interface AutopilotRuntimeStatus {
  runtime_id: string;
  state: AutopilotRuntimeState;
  scope: string;
  started_at: string;
  updated_at: string;
  current_task_id?: string | null;
  last_event?: string | null;
}

export interface AutopilotStatusSnapshot {
  running: boolean;
  runtime: AutopilotRuntimeStatus | null;
  summary: {
    pending: number;
    in_progress: number;
    done: number;
    blocked: number;
    total: number;
  };
  current_task: AutopilotTaskStatus | null;
  next_task: AutopilotTaskStatus | null;
}

type Listener = () => void;

let snapshot: AutopilotStatusSnapshot = {
  running: false,
  runtime: null,
  summary: {
    pending: 0,
    in_progress: 0,
    done: 0,
    blocked: 0,
    total: 0,
  },
  current_task: null,
  next_task: null,
};

let listeners: Listener[] = [];
let started = false;

function emit() {
  for (const listener of listeners) listener();
}

async function refresh() {
  try {
    snapshot = await apiJson<AutopilotStatusSnapshot>("/api/autopilot/status");
    emit();
  } catch {
    // Preserve last-known state if the status endpoint is temporarily unavailable.
  }
}

function ensurePolling() {
  if (started) return;
  started = true;
  void refresh();
  window.setInterval(() => {
    void refresh();
  }, 4000);
}

export function useAutopilotStatus(): AutopilotStatusSnapshot {
  const [state, setState] = useState(snapshot);

  useEffect(() => {
    ensurePolling();
    const listener = () => setState({ ...snapshot });
    listeners.push(listener);
    return () => {
      listeners = listeners.filter((entry) => entry !== listener);
    };
  }, []);

  return state;
}
